/**
 * Operator call detail page — a single call's CANONICAL record (real
 * diagnostics: id, hotel, language, state, duration — never transcript/guest
 * content) plus an optional provider-diagnostics block (flag-gated, clearly
 * labelled SAMPLE: raw payload + tool timeline). Gated by
 * `admin:cross_tenant_read`.
 *
 * NOTE: there is no `GET /operator/calls/:id` yet, so the call is found in the
 * recent diagnostics window (`useOperatorCalls({ limit: 200 })`). FOLLOW-UP:
 * add a real single-call operator endpoint so this does not depend on the call
 * being in the recent page.
 */

import { type OperatorCall, useOperatorCalls } from '@hostesa/api-client';
import { useFlag } from '@hostesa/feature-flags';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  AudioPlayer,
  Button,
  CallTranscript,
  Card,
  cn,
  ErrorState,
  LanguagePill,
  LoadingState,
  type PillTone,
  StatusPill,
  StatusTabs,
  type TranscriptSegment,
} from '@hostesa/ui';
import { type ReactNode, useState } from 'react';

const PAGE_WRAP = 'mx-auto w-full max-w-5xl px-4 py-6 sm:px-8 sm:py-8';
const EYEBROW =
  'mb-1 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]';
const SECTION_EYEBROW =
  'mb-3 flex items-center gap-2.5 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)] before:h-px before:w-[18px] before:bg-[var(--color-line)]';
const SAMPLE_NOTE =
  'mb-2 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]';

const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function callTone(status: string): PillTone {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'alert';
  if (status === 'handoff_requested' || status === 'human_active') return 'accent';
  return 'neutral';
}
function toSeconds(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function langCode(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim().toUpperCase() : null;
}
function shortId(id: string): string {
  return id.slice(0, 8);
}
/** A label → value definition row. Values may be plain strings or nodes. */
function DefRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-[var(--color-line-2)] py-2.5 text-[13.5px] first:border-t-0">
      <span className="text-[var(--color-stone-mid)]">{label}</span>
      <span
        className={cn(
          'text-right font-medium text-[var(--color-charcoal)]',
          mono && 'font-[var(--font-mono)] text-[12px] font-normal',
        )}
      >
        {value}
      </span>
    </div>
  );
}

export interface CallDetailPageProps {
  callId: string;
  /** Optional back handler (router wires navigation). */
  onBack?: () => void;
}

export function CallDetailPage({ callId, onBack }: CallDetailPageProps) {
  const { t } = useT('backoffice');
  const { formatDuration } = useFormatters(VIEWER_TZ);
  const showDiagnostics = useFlag('backoffice_provider_diagnostics');
  const [activeTab, setActiveTab] = useState('overview');

  // No GET /operator/calls/:id yet — find the call in the recent window.
  const calls = useOperatorCalls({ limit: 200 });

  if (calls.isLoading) {
    return (
      <section data-testid="call-detail-page" className={PAGE_WRAP}>
        <LoadingState data-testid="call-detail-loading" title={t('oversight.loading')} />
      </section>
    );
  }
  if (calls.isError) {
    return (
      <section data-testid="call-detail-page" className={PAGE_WRAP}>
        <ErrorState
          data-testid="call-detail-error"
          title={t('oversight.error.title')}
          description={t('oversight.error.description')}
        />
      </section>
    );
  }

  const call: OperatorCall | undefined = (calls.data?.items ?? []).find((c) => c.id === callId);

  if (!call) {
    return (
      <section data-testid="call-detail-page" className={PAGE_WRAP}>
        <ErrorState
          data-testid="call-detail-not-found"
          title={t('oversight.callDetailPage.notFound.title')}
          description={t('oversight.callDetailPage.notFound.description')}
        />
      </section>
    );
  }

  const dur = toSeconds(call.durationSeconds);
  const lang = langCode(call.language);
  const sid = shortId(call.id);

  // SAMPLE provider payload — a small, clearly-labelled object (never live).
  const samplePayload = {
    _note: 'sample — not live provider data',
    callRef: `prov_${sid}`,
    session: `sess_${sid}`,
    region: 'eu-central-1',
    stt: { engine: 'sample-stt', lang: lang ?? 'xx' },
    tts: { voice: 'sample-voice-a' },
  };

  // SAMPLE Bulgarian transcript — clearly-labelled sample guest content, never
  // a real recording. Rendered only behind `showDiagnostics`.
  const sampleTranscript: TranscriptSegment[] = [
    {
      id: 'seg-1',
      speaker: 'assistant',
      text: 'Добър ден, хотел „Корнелия“, как мога да Ви помогна?',
      at: '00:00',
    },
    {
      id: 'seg-2',
      speaker: 'user',
      text: 'Здравейте, имате ли паркинг към хотела?',
      at: '00:09',
    },
    {
      id: 'seg-3',
      speaker: 'assistant',
      text: 'Да, разполагаме с охраняем паркинг. Цената е 10 лева на нощувка.',
      at: '00:18',
    },
    {
      id: 'seg-4',
      speaker: 'user',
      text: 'Чудесно. Бих искал да резервирам двойна стая за следващия петък.',
      at: '00:27',
    },
    {
      id: 'seg-5',
      speaker: 'assistant',
      text: 'С удоволствие. Ще проверя наличността и ще потвърдя резервацията.',
      at: '00:36',
    },
  ];

  // Section tabs. "Overview" (the real canonical record) is always present;
  // recording / transcript / diagnostics are flag-gated sample content.
  const tabs = [
    { id: 'overview', label: t('oversight.callDetailPage.tabs.overview') },
    ...(showDiagnostics
      ? [
          { id: 'recording', label: t('oversight.callDetailPage.tabs.recording') },
          { id: 'transcript', label: t('oversight.callDetailPage.tabs.transcript') },
          { id: 'diagnostics', label: t('oversight.callDetailPage.tabs.diagnostics') },
        ]
      : []),
  ];
  const tab = tabs.some((x) => x.id === activeTab) ? activeTab : 'overview';

  return (
    <section data-testid="call-detail-page" className={PAGE_WRAP}>
      {/* Header --------------------------------------------------------------- */}
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className={EYEBROW}>
            {`${t('oversight.callDetailPage.eyebrow')} › ${t('oversight.callDetailPage.crumb')}`}
          </p>
          <h1 className="font-[var(--font-display)] text-2xl text-[var(--color-charcoal)]">
            {t('oversight.callDetailPage.title', { shortId: sid })}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-col">
            <span className="font-[var(--font-mono)] text-[9px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]">
              {t('oversight.callDetailPage.meta.hotel')}
            </span>
            <span className="text-[12.5px] font-medium text-[var(--color-charcoal)]">
              {call.hotelSlug ?? t('oversight.notAvailable')}
            </span>
          </div>
          {showDiagnostics && (
            <div className="flex flex-col" data-testid="call-detail-meta-cost">
              <span className="font-[var(--font-mono)] text-[9px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]">
                {`${t('oversight.callDetailPage.meta.cost')} · ${t('oversight.callDetailPage.sampleNote')}`}
              </span>
              <span className="text-[12.5px] font-medium text-[var(--color-charcoal)]">
                €0.42 (sample)
              </span>
            </div>
          )}
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="call-detail-back-btn"
              onClick={onBack}
            >
              {t('oversight.callDetailPage.backToCalls')}
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" data-testid="call-detail-recording-btn">
            {t('oversight.callDetailPage.recording')}
          </Button>
          <Button type="button" variant="outline" size="sm" data-testid="call-detail-export-btn">
            {t('oversight.callDetailPage.exportLog')}
          </Button>
        </div>
      </header>

      {/* Internal diagnostic notice (warn-wash) ------------------------------ */}
      <div
        data-testid="call-detail-internal-notice"
        role="note"
        className="mb-6 rounded-[var(--radius-md)] border border-[rgba(176,108,78,0.28)] bg-[var(--color-warn-wash)] px-4 py-3 text-sm text-[var(--color-warn)]"
      >
        <strong className="font-[var(--font-display)]">
          {t('oversight.callDetailPage.internalNotice.label')}
        </strong>
        <p className="mt-1">{t('oversight.callDetailPage.internalNotice.body')}</p>
      </div>

      {/* Identity strip ------------------------------------------------------ */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="font-[var(--font-mono)] text-sm text-[var(--color-charcoal)]">{sid}</span>
        <StatusPill
          dot
          tone={callTone(call.status)}
          label={t(`oversight.calls.status.${call.status}`)}
        />
        {lang && <LanguagePill code={lang} />}
        {showDiagnostics && (
          <span
            data-testid="call-detail-provider-pill"
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-accent-wash)] px-2.5 py-1 font-[var(--font-mono)] text-[10px] text-[var(--color-stone-700)]"
          >
            sample-voice-rtc · sample
          </span>
        )}
        <span
          className={cn(
            'ml-auto',
            'font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]',
          )}
        >
          {`${t('oversight.callDetailPage.trace')}: ${call.id}`}
        </span>
      </div>

      {/* Section tabs --------------------------------------------------------- */}
      {tabs.length > 1 && (
        <StatusTabs
          data-testid="call-detail-tabs"
          tabs={tabs}
          value={tab}
          onChange={setActiveTab}
          className="mb-6"
        />
      )}

      {/* Overview tab — canonical record (REAL) ------------------------------ */}
      {tab === 'overview' && (
        <Card data-testid="call-detail-canonical" className="mb-6 p-4 sm:p-5">
          <DefRow label={t('oversight.callDetailPage.canonical.callId')} value={call.id} mono />
          <DefRow
            label={t('oversight.callDetailPage.canonical.hotel')}
            value={call.hotelSlug ?? t('oversight.notAvailable')}
          />
          <DefRow
            label={t('oversight.callDetailPage.canonical.caller')}
            value={t('oversight.callDetailPage.canonical.callerRedacted')}
          />
          <DefRow
            label={t('oversight.callDetailPage.canonical.language')}
            value={lang ?? t('oversight.notAvailable')}
          />
          <DefRow
            label={t('oversight.callDetailPage.canonical.state')}
            value={
              <StatusPill
                dot
                tone={callTone(call.status)}
                label={t(`oversight.calls.status.${call.status}`)}
              />
            }
          />
          <DefRow
            label={t('oversight.callDetailPage.canonical.duration')}
            value={dur !== null ? formatDuration(dur) : t('oversight.notAvailable')}
            mono
          />
          {showDiagnostics && (
            <DefRow
              label={`${t('oversight.callDetailPage.canonical.costEstimate')} · ${t('oversight.callDetailPage.sampleNote')}`}
              value="€0.42 (sample)"
              mono
            />
          )}
        </Card>
      )}

      {/* Recording tab — flag-gated, labelled SAMPLE guest content ---------- */}
      {tab === 'recording' && (
        <div className="mb-6">
          {/* No durationLabel: let the player read the actual sample-audio
             length from the <audio> element so the readout never disagrees
             with what's audible. (A real recording's length == the call's.) */}
          <AudioPlayer
            data-testid="call-detail-recording-player"
            src="/sample-call.wav"
            downloadHref="/sample-call.wav"
            title={t('oversight.callDetailPage.recordingSection.title')}
            sampleNote={t('oversight.callDetailPage.sampleNote')}
          />
        </div>
      )}

      {/* Transcript tab ------------------------------------------------------ */}
      {tab === 'transcript' && (
        <div className="mb-6" data-testid="call-detail-transcript">
          <p className={SAMPLE_NOTE}>{t('oversight.callDetailPage.sampleNote')}</p>
          <CallTranscript data-testid="call-detail-transcript-body" segments={sampleTranscript} />
        </div>
      )}

      {/* Diagnostics tab — flag-gated, labelled SAMPLE --------------------- */}
      {tab === 'diagnostics' && (
        <>
          <p className={SECTION_EYEBROW}>{t('oversight.callDetailPage.diagnostics.title')}</p>
          <Card data-testid="call-detail-diagnostics" className="mb-6 p-4 sm:p-5">
            <p className={SAMPLE_NOTE}>{t('oversight.callDetailPage.sampleNote')}</p>
            <DefRow
              label={t('oversight.callDetailPage.diagnostics.provider')}
              value="sample-voice-rtc"
              mono
            />
            <DefRow
              label={t('oversight.callDetailPage.diagnostics.session')}
              value={`sess_${sid}`}
              mono
            />
            <DefRow label={t('oversight.callDetailPage.diagnostics.stt')} value="sample-stt" mono />
            <DefRow
              label={t('oversight.callDetailPage.diagnostics.tts')}
              value="sample-voice-a"
              mono
            />
            <DefRow
              label={t('oversight.callDetailPage.diagnostics.webhook')}
              value="200 · 41ms (sample)"
              mono
            />
          </Card>

          {/* Raw provider payload (dark code block) */}
          <p className={SECTION_EYEBROW}>{t('oversight.callDetailPage.rawPayload.title')}</p>
          <div className="mb-6">
            <p className={SAMPLE_NOTE}>{t('oversight.callDetailPage.sampleNote')}</p>
            <pre
              data-testid="call-detail-raw-payload"
              className="overflow-x-auto rounded-[var(--radius-lg)] bg-[var(--color-charcoal)] p-4 font-[var(--font-mono)] text-[11.5px] leading-relaxed text-[var(--color-alabaster)]"
            >
              {JSON.stringify(samplePayload, null, 2)}
            </pre>
          </div>

          {/* Tool timeline */}
          <p className={SECTION_EYEBROW}>{t('oversight.callDetailPage.toolTimeline.title')}</p>
          <Card data-testid="call-detail-tool-timeline" className="p-4 sm:p-5">
            <p className={SAMPLE_NOTE}>{t('oversight.callDetailPage.sampleNote')}</p>
            <ol className="flex flex-col gap-3">
              {[
                { id: 'lookup_booking', at: '00:03', note: 'ok (sample)' },
                { id: 'check_availability', at: '00:11', note: 'ok (sample)' },
                { id: 'create_request', at: '00:24', note: 'queued (sample)' },
              ].map((ev) => (
                <li
                  key={ev.id}
                  data-testid={`call-detail-tool-${ev.id}`}
                  className="flex items-baseline gap-3 text-[13px]"
                >
                  <span className="w-12 shrink-0 font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                    {ev.at}
                  </span>
                  <span className="font-[var(--font-mono)] text-[12px] text-[var(--color-charcoal)]">
                    {ev.id}
                  </span>
                  <span className="ml-auto font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                    {ev.note}
                  </span>
                </li>
              ))}
            </ol>
          </Card>
        </>
      )}
    </section>
  );
}
